// src/components/Student/JoinClassModal.tsx
import React, { useState } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceLowest:    '#ffffff',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
    error:            '#b41340',
};
 
interface JoinClassModalProps {
    onClose: () => void;
    onClassJoined: () => void;
}
 
const JoinClassModal: React.FC<JoinClassModalProps> = ({ onClose, onClassJoined }) => {
    const { currentUser } = useAuth();
    const [joinCode, setJoinCode] = useState('');
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [success, setSuccess]   = useState('');
 
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) { setError('You must be logged in to join a class'); return; }
 
        // Normalize: remove spaces, dashes, convert to uppercase
        const normalizedCode = joinCode.trim().replace(/[-\s]/g, '').toUpperCase();
 
        if (normalizedCode.length !== 6) {
            setError('Invalid code — please enter the 6-character code your teacher gave you.');
            return;
        }
 
        setLoading(true);
        setError('');
 
        try {
            const snap = await getDocs(query(
                collection(db, 'classes'),
                where('joinCode', '==', normalizedCode)
            ));
 
            if (snap.empty) {
                setError('Code not found. Double-check with your teacher and try again.');
                setLoading(false);
                return;
            }
 
            const classDoc = snap.docs[0];
            const classData = classDoc.data();
 
            if (classData.students?.[currentUser.uid]) {
                setError('You are already enrolled in this class!');
                setLoading(false);
                return;
            }
 
            // Add student to class
            await updateDoc(doc(db, 'classes', classDoc.id), {
                [`students.${currentUser.uid}`]: true,
            });
 
            // Add class to user's classes array
            await setDoc(doc(db, 'users', currentUser.uid), {
                classes: [...(classData.classes || []), classDoc.id],
            }, { merge: true });
 
            setSuccess(`You joined ${classData.name}!`);
            setTimeout(() => { onClassJoined(); }, 1200);
 
        } catch (err: any) {
            console.error('Error joining class:', err);
            setError(err.message || 'Failed to join class. Please try again.');
        } finally {
            setLoading(false);
        }
    };
 
    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '440px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.18)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
                {success ? (
                    /* ── Success state ─────────────────────────────────────── */
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🎉</div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.4rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                            {success}
                        </h2>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: 0 }}>
                            Your teacher's assignments will appear on your dashboard.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* ── Header ─────────────────────────────────────────── */}
                        <div style={{ marginBottom: '28px' }}>
                            <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onBackground, margin: '0 0 6px 0' }}>
                                Join a Class
                            </h2>
                            <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: 0 }}>
                                Enter the 6-character code your teacher gave you.
                            </p>
                        </div>
 
                        {/* ── Error ──────────────────────────────────────────── */}
                        {error && (
                            <div style={{ background: '#fef2f2', border: `1.5px solid ${C.error}30`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: C.error, fontSize: '0.875rem', fontWeight: '600' }}>
                                {error}
                            </div>
                        )}
 
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '28px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.8rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Class Code
                                </label>
 
                                {/* Big monospace code input */}
                                <input
                                    type="text"
                                    value={joinCode}
                                    onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                    placeholder="KEATEJ"
                                    required
                                    maxLength={6}
                                    autoFocus
                                    style={{
                                        width: '100%',
                                        padding: '16px',
                                        border: `2px solid ${joinCode.length === 6 ? C.primary : C.outlineVariant}`,
                                        borderRadius: '14px',
                                        fontSize: '2rem',
                                        fontFamily: 'monospace',
                                        fontWeight: '900',
                                        textAlign: 'center',
                                        letterSpacing: '6px',
                                        boxSizing: 'border-box',
                                        textTransform: 'uppercase',
                                        outline: 'none',
                                        background: joinCode.length === 6 ? C.primaryContainer : C.surfaceLow,
                                        color: C.primary,
                                        transition: 'all 0.15s',
                                    }}
                                />
 
                                {/* Character counter */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                                    <span style={{ fontSize: '0.78rem', color: C.onSurfaceVariant }}>
                                        Letters and numbers only
                                    </span>
                                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: joinCode.length === 6 ? C.primary : C.onSurfaceVariant }}>
                                        {joinCode.length}/6
                                    </span>
                                </div>
                            </div>
 
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={loading}
                                    style={{ flex: 1, padding: '12px', background: C.surfaceLowest, color: C.onSurfaceVariant, border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (!loading) e.currentTarget.style.background = C.surfaceLow; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLowest; }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || joinCode.length !== 6}
                                    style={{ flex: 1, padding: '12px', background: (loading || joinCode.length !== 6) ? C.outlineVariant : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: (loading || joinCode.length !== 6) ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: (loading || joinCode.length !== 6) ? 'none' : `0 4px 12px ${C.primary}40`, transition: 'all 0.15s' }}
                                >
                                    {loading ? 'Joining...' : 'Join Class'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};
 
export default JoinClassModal;